import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
} from 'react';

export interface ChartExportData {
  chart: any;
  categories: string[];
  title: string;
  startDate: string;
  endDate: string;
}

type ChartExportDataGetter = () => ChartExportData | null;

interface DashboardDownloadContextType {
  registerChart: (id: string, getExportData: ChartExportDataGetter) => void;
  unregisterChart: (id: string) => void;
  getRegisteredCharts: () => Map<string, ChartExportDataGetter>;
}

const DashboardDownloadContext = createContext<DashboardDownloadContextType | null>(null);

export const useDashboardDownloadContext = () => useContext(
  DashboardDownloadContext,
);

interface DashboardDownloadProviderProps {
  children: React.ReactNode;
}

export const DashboardDownloadProvider: React.FC<DashboardDownloadProviderProps> = ({
  children,
}) => {
  const chartsRef = useRef<Map<string, ChartExportDataGetter>>(new Map());

  const registerChart = useCallback((id: string, getExportData: ChartExportDataGetter) => {
    chartsRef.current.set(id, getExportData);
  }, []);

  const unregisterChart = useCallback((id: string) => {
    chartsRef.current.delete(id);
  }, []);

  const getRegisteredCharts = useCallback(
    () => new Map(chartsRef.current),
    [],
  );

  const value: DashboardDownloadContextType = {
    registerChart,
    unregisterChart,
    getRegisteredCharts,
  };

  return (
    <DashboardDownloadContext.Provider value={value}>
      {children}
    </DashboardDownloadContext.Provider>
  );
};
